import { Http } from '@mygpt/app/http';

export interface AuthLoginParams {
  username?: string;
  password: string;
  email?: string;
}

export interface AuthRegisterParams {
  name: string;
  email: string;
  password: string;
}

export interface ResendVerificationParams {
  email: string;
}

export interface OnPremAuthLoginParams {
  password: string;
}

/**
 * MFA 挑战响应：authorize / google_verify 在需要二次验证时返回（HTTP 200）
 * 注意：验证码已随本响应自动发出，无需再调 mfaSend；resend_after 秒内应禁用「重新发送」
 */
export interface MfaRequiredResponse {
  mfa_required: true;
  mfa_token: string;
  /** 当前仅 "email" */
  methods: string[];
  /** 掩码后的邮箱，如 n***o@atre.co.jp */
  email_masked: string;
  /** 初始重发冷却秒数 */
  resend_after: number;
}

/** authorize 响应：登录成功 token 或需要 MFA 二次验证 */
export type AuthAuthorizeResult = AuthTokenResponse | MfaRequiredResponse;

/** google_verify 响应：登录成功 token 或需要 MFA 二次验证 */
export type GoogleVerifyResult = GoogleVerifyResponse | MfaRequiredResponse;

/** 判别 200 响应是否为 MFA 挑战分支 */
export const isMfaRequired = (
  data: AuthAuthorizeResult | GoogleVerifyResult | undefined
): data is MfaRequiredResponse => {
  return !!data && (data as MfaRequiredResponse).mfa_required === true;
};

/**
 * 帐号密码登录
 * @param params
 * @returns
 */
export const authLogin = (params: AuthLoginParams) => {
  const { ...rest } = params;
  const { post } = Http.use();
  if (!rest.username && rest.email) {
    rest.username = rest.email;
  }
  return post<AuthAuthorizeResult>(`/oauth/authorize`, rest, {
    skipErrorNotification: true,
  });
};

/**
 * MFA 验证码校验（匿名接口）
 * 成功响应与 authorize 成功完全同构，复用登录完成逻辑
 * 错误码：MFA_CODE_INVALID(attempts_left) / MFA_CODE_EXPIRED / MFA_TOKEN_EXPIRED
 */
export const mfaVerify = (params: { mfa_token: string; code: string }) => {
  const { post } = Http.use();
  return post<AuthTokenResponse>(`/auth/mfa/verify`, params, {
    headers: { notNeedToken: 'true' },
    // 401 时跳过全局 loginExpired，让登录页自行按错误码提示
    skipErrorNotification: true,
  });
};

/**
 * MFA 重新发送验证码（匿名接口）
 * 204 已重发；429 MFA_RESEND_COOLDOWN(retry_after) / MFA_SEND_LIMIT；401 MFA_TOKEN_EXPIRED
 */
export const mfaSend = (params: { mfa_token: string }) => {
  const { post } = Http.use();
  return post<void>(`/auth/mfa/send`, params, {
    headers: { notNeedToken: 'true' },
    skipErrorNotification: true,
  });
};

/**
 * 帐号注册
 */
export const authRegister = (params: AuthRegisterParams) => {
  const { post } = Http.use();
  return post(`/auth/register`, params, {
    headers: { notNeedToken: 'true' },
    skipErrorNotification: true,
  });
};

/**
 * 重新发送邮箱验证
 */
export const resendVerification = (params: ResendVerificationParams) => {
  const { post } = Http.use();
  return post(`/auth/resend-verification`, params, {
    headers: { notNeedToken: 'true' },
  });
};

/**
 * 请求重置密码邮件
 */
export const requestPasswordReset = (params: { email: string }) => {
  const { post } = Http.use();
  return post(`/auth/request-reset`, params, {
    headers: { notNeedToken: 'true' },
  });
};

/**
 * 将用户邮箱设置为已验证
 */
export const verifyEmail = (params: { token: string }) => {
  const { post } = Http.use();
  return post(`/auth/verify-email`, params, {
    headers: { notNeedToken: 'true' },
    skipErrorNotification: true,
  });
};

/**
 * 使用令牌重置密码
 */
export const resetPassword = ({ resetToken, newPassword }: { resetToken: string; newPassword: string }) => {
  const { post } = Http.use();
  return post(
    `/auth/reset-password`,
    {
      token: resetToken,
      new_password: newPassword,
    },
    {
      // reset token 无效/过期返回 401 时，跳过全局 loginExpired 处理，让页面自行提示错误
      headers: { notNeedToken: 'true' },
      skipErrorNotification: true,
    }
  );
};

/**
 * 修改当前密码
 */
export const changePassword = ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
  const { post } = Http.use();
  return post(`/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
  });
};

/**
 * 密码规则对象(与后端 security-policy 的 password_rules 逐键一致)
 */
export interface PasswordRules {
  /** 必须包含大小写字母 + 数字 */
  require_case_digit: boolean;
  /** 必须包含特殊字符 */
  require_special: boolean;
  /** 长度不小于 8 位 */
  min_length_8: boolean;
  /** 90 天内更新密码 */
  rotate_90d: boolean;
  /** 禁止重用最近 N 次密码(开关) */
  reuse_forbidden: boolean;
  /** 禁止重用的次数 N(1~24) */
  reuse_n: number;
  /** 登录失败 N 次锁定账户(开关) */
  lockout_enabled: boolean;
  /** 锁定阈值 N(1~10) */
  lockout_n: number;
  /** 密码有效期设为无期限 */
  no_expiry: boolean;
}

export interface PasswordPolicyResponse {
  /** true = 全局策略未配置,维持现网(legacy)行为,此时 rules 为 null */
  legacy: boolean;
  rules: PasswordRules | null;
}

/**
 * 获取密码策略(表单规则渲染 + 前端预校验)
 * - 匿名可调:注册/重置页返回全局规则
 * - 带 Bearer token 调用:返回该用户生效规则(企业覆盖优先);现有 client 有 token 时会自动携带
 */
export const getPasswordPolicy = () => {
  const { get } = Http.use();
  return get<PasswordPolicyResponse>(`/auth/password-policy`, {
    // 策略拉取失败不弹全局错误,由调用方兜底为 legacy 模式
    skipErrorNotification: true,
  });
};
export interface LoginTokenPayload {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresIn: number;
  tokenType: string;
}

export interface AuthTokenResponse {
  loginToken?: LoginTokenPayload;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
  roles?: string[];
}

export interface GoogleVerifyResponse {
  loginToken?: LoginTokenPayload;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
  redirect_url?: string;
  redirectUri?: string;
}

/**
 * Google OAuth 回调验证
 */
export const googleVerify = (params: { code: string; redirectURI: string }) => {
  const { get } = Http.use();

  return get<GoogleVerifyResult>(`/auth/google_verify`, {
    params: {
      code: params.code,
      redirect_uri: btoa(params.redirectURI),
    },
  });
};
